import { useState } from "react";
import { useMutation, gql } from "@apollo/client";
import { TaskType, type Task, type TaskExtended } from "@/types";
import { useUsers } from "@/hooks/useUsers";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Combobox } from "@/components/ui/combobox";
import { DeadlineField } from "@/components/tasks/DeadlineField";
import { RecurrenceFields } from "@/components/tasks/RecurrenceFields";
import { SeriesScopeDialog } from "@/components/tasks/SeriesScopeDialog";
import { InspectionTopicRowList } from "@/components/tasks/InspectionTopicRowList";
import {
  InspectionTopicRewriteSteps,
  inspectionTopicRewriteStepDescription,
} from "@/components/tasks/InspectionTopicRewriteSteps";
import { useInspectionTopicRewriteFlow } from "@/hooks/useInspectionTopicRewriteFlow";
import {
  addTopicRow,
  moveTopicRow,
  removeTopicRow,
  seedTopicRows,
  topicRowsAreDirty,
  updateTopicRowName,
  validateTopicRows,
  type EditableTopicRow,
} from "@/lib/inspectionTopicRows";
import {
  UPDATE_TASK_SERIES,
  UPDATE_TASK_SERIES_RULE,
} from "@/graphql/mutations";
import { emptyRecurrence, type RecurrenceFormState } from "@/lib/recurrence";
import { formatResponsible } from "@/lib/responsibleUser";
import { SpinnerIcon } from "@/lib/icons";

const UPDATE_TASK = gql`
  mutation UpdateTask($input: TaskUpdateInput!) {
    updateTask(input: $input) {
      success
      message
      task {
        id
        title
        description
        status
        taskType
        projectId
        sectionId
        locationId
        dueDate
        responsibleUserId
        createdByUserId
        responsibleUser {
          id
          firstName
          lastName
          azureObjectId
          substituteUserId
          substituteFirstName
          substituteLastName
        }
        inheritedResponsibleUser {
          id
          firstName
          lastName
          substituteUserId
          substituteFirstName
          substituteLastName
        }
        version
        createdAt
        updatedAt
        procurementPackage {
          code
          name
        }
        section {
          id
          name
          procurementPackage {
            code
            name
          }
        }
        location {
          id
          name
          locationType
          hierarchyPath
        }
      }
    }
  }
`;

/**
 * Combobox value standing for "hold no override, inherit instead". Cannot be
 * `null` or `""`: those already mean "nothing selected", which the combobox
 * renders as the placeholder, and inheritance is an explicit choice that has to
 * render as a selected one.
 */
const INHERIT_VALUE = "__inherit__";

interface TaskMetadataEditDialogProps {
  task: TaskExtended | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Called with the updated task after a successful save. Lets the parent
   * refresh its `selectedTask` snapshot so the underlying TaskDialog
   * re-renders with the new values (Apollo's normalized cache is updated
   * by the mutation, but the parent's useState snapshot is not).
   */
  onSaved?: (task: TaskExtended) => void;
  /**
   * The task's subtasks and their load state, for the embedded
   * tarkastuskohdat section (INSPECTION_MAIN_TASK only). An unloaded list
   * must never be mistaken for "no topics" — see `topicsLoaded` below.
   */
  subtasks: Task[];
  subtasksLoading: boolean;
  /** Called after a tarkastuskohdat rewrite completes, to refetch subtasks and the task detail. */
  onTopicsRewritten?: () => void;
}

export function TaskMetadataEditDialog({
  task,
  open,
  onOpenChange,
  onSaved,
  subtasks,
  subtasksLoading,
  onTopicsRewritten,
}: TaskMetadataEditDialogProps) {
  const { users } = useUsers();
  const [updateTaskMutation] = useMutation(UPDATE_TASK);
  const [updateSeriesMutation] = useMutation(UPDATE_TASK_SERIES);
  const [updateRuleMutation] = useMutation(UPDATE_TASK_SERIES_RULE);

  const [formData, setFormData] = useState({
    title: "",
    responsibleUserId: "",
    dueDate: "",
  });
  const [localVersion, setLocalVersion] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Recurrence. The rule is series-wide by definition, so changing it never
  // goes through the scope prompt; only the content fields above do.
  const [recurrence, setRecurrence] = useState<RecurrenceFormState>(emptyRecurrence());
  const [scopeOpen, setScopeOpen] = useState(false);
  const [ruleSummary, setRuleSummary] = useState<string | null>(null);

  // Hydrated during render rather than in an effect: an effect runs after the
  // first paint, so opening the dialog on a second task flashed the previous
  // one's values for a frame — most visibly as the highlighted day in the
  // calendar. Updates while closed are still skipped, so a background refetch
  // cannot clobber in-progress edits.
  const hydrationKey = task
    ? [
        task.id,
        task.version,
        task.title,
        task.responsibleUserId ?? "",
        task.dueDate ?? "",
        // A rule change bumps the series version, so the recurrence controls
        // re-hydrate rather than showing the rule that was just replaced.
        task.recurrenceSeries?.version ?? "",
      ].join("|")
    : null;
  const [hydratedKey, setHydratedKey] = useState<string | null>(null);

  if (!open) {
    // Cleared on close so reopening the same task discards unsaved edits.
    if (hydratedKey !== null) setHydratedKey(null);
  } else if (task && hydrationKey !== hydratedKey) {
    setHydratedKey(hydrationKey);
    setFormData({
      title: task.title,
      responsibleUserId: task.responsibleUserId ?? "",
      dueDate: task.dueDate ? task.dueDate.split("T")[0] : "",
    });
    setRecurrence(
      task.recurrenceSeries
        ? {
            enabled: true,
            frequency: task.recurrenceSeries.frequency,
            weekday: task.recurrenceSeries.weekday ?? 0,
            weekOfMonth: task.recurrenceSeries.weekOfMonth ?? 1,
            endDate: task.recurrenceSeries.endDate,
          }
        : emptyRecurrence(),
    );
    setLocalVersion(task.version);
    setRuleSummary(null);
    setError(null);
  }

  // Tarkastuskohdat — shown only for an INSPECTION_MAIN_TASK with a linked
  // Congrid inspection, and only once its subtasks have loaded. An unloaded
  // list must never be mistaken for "no topics": saving from an empty editor
  // would wipe the inspection's real topics.
  const showTopics =
    task?.taskType === TaskType.INSPECTION_MAIN_TASK &&
    !!task?.externalInspectionPlatformInspectionId;
  const topicsLoaded = showTopics && !subtasksLoading;

  const [rows, setRows] = useState<EditableTopicRow[]>([]);
  const [seededRows, setSeededRows] = useState<EditableTopicRow[]>([]);
  const [topicsHydratedKey, setTopicsHydratedKey] = useState<string | null>(null);
  const [topicsFlowActive, setTopicsFlowActive] = useState(false);
  const topicFlow = useInspectionTopicRewriteFlow({ open });

  // Same during-render hydration technique as the fields above, but keyed on
  // the seeded rows' own content: subtasks load on a separate, later timeline
  // than the rest of the task, so this re-seeds only once they arrive or
  // genuinely change — never on a keystroke while the user is editing them.
  const topicSeed = topicsLoaded ? seedTopicRows(subtasks) : [];
  const topicsKey = topicsLoaded
    ? `${task!.id}|${topicSeed.map((row) => `${row.key}:${row.name}`).join(",")}`
    : null;

  if (!open) {
    if (topicsHydratedKey !== null) setTopicsHydratedKey(null);
    if (topicsFlowActive) setTopicsFlowActive(false);
  } else if (topicsLoaded && topicsKey !== topicsHydratedKey) {
    setTopicsHydratedKey(topicsKey);
    setRows(topicSeed);
    setSeededRows(topicSeed);
  }

  const rowsValidationError = topicsLoaded ? validateTopicRows(rows) : null;
  const rowsDirty = topicsLoaded && topicRowsAreDirty(seededRows, rows);

  // Inspection tasks take their deadline from Sitedrive, so the field is left
  // out entirely rather than shown as read-only.
  const canEditDueDate = task?.taskType !== TaskType.INSPECTION_MAIN_TASK;

  const hasResponsibleOverride = !!task?.responsibleUserId;
  // The incoming person if the override is dropped. Read from the dedicated
  // field, not `responsibleUser`: while an override exists that resolver
  // returns the override holder, so the placeholder would name the person being
  // removed and only correct itself after the server recomputed on save.
  const inheritedResponsible = formatResponsible(task?.inheritedResponsibleUser);
  // Null means nothing resolves without the override, so it cannot be dropped
  // and the inherit row is left out of the combobox entirely.
  const canClearResponsible = !!task?.inheritedResponsibleUser;

  const inheritOption = canClearResponsible
    ? {
        value: INHERIT_VALUE,
        label: "Periytä",
        description: inheritedResponsible,
        // The closed field names the person who actually holds the task, not the
        // mechanism that put them there — but muted, because the task carries no
        // tehtäväkohtainen vastuuhenkilö of its own. Falls back to "Periytä" when
        // the inherited user has no renderable name.
        triggerLabel: inheritedResponsible || undefined,
        triggerMuted: true,
      }
    : undefined;

  /** The sparse update input for whatever the user actually changed. */
  const buildInput = (): Record<string, unknown> | null => {
    if (!task) return null;

    const input: Record<string, unknown> = {
      id: task.id,
      version: localVersion,
    };

    const trimmedTitle = formData.title.trim();
    if (trimmedTitle && trimmedTitle !== task.title) {
      input.title = trimmedTitle;
    }

    if (formData.responsibleUserId) {
      if (formData.responsibleUserId !== task.responsibleUserId) {
        input.responsibleUserId = formData.responsibleUserId;
      }
    } else if (hasResponsibleOverride) {
      // Falling back to the inherited responsible. A null responsibleUserId
      // cannot express this — the backend drops nulls — hence the sentinel.
      input.clearResponsibleUser = true;
    }

    const currentDueDate = task.dueDate ? task.dueDate.split("T")[0] : "";
    if (canEditDueDate && formData.dueDate !== currentDueDate) {
      input.dueDate = formData.dueDate || null;
    }

    return Object.keys(input).length > 2 ? input : null;
  };

  const ruleChanged = (): boolean => {
    const series = task?.recurrenceSeries;
    if (!series) return false;
    return (
      recurrence.frequency !== series.frequency ||
      recurrence.weekday !== (series.weekday ?? 0) ||
      recurrence.weekOfMonth !== (series.weekOfMonth ?? 1) ||
      recurrence.endDate !== series.endDate
    );
  };

  /**
   * Applies a rule change and reports what it did to the tasks that already
   * existed. Regeneration may have soft-deleted this very task, so the caller
   * closes the dialog rather than continuing to a content save.
   */
  const commitRule = async (): Promise<boolean> => {
    if (!task) return false;
    setIsSaving(true);
    setError(null);
    try {
      const result = await updateRuleMutation({
        variables: {
          input: {
            id: task.id,
            version: localVersion,
            frequency: recurrence.frequency,
            weekday:
              recurrence.frequency === "DAILY_WEEKDAYS" ? null : recurrence.weekday,
            weekOfMonth:
              recurrence.frequency === "MONTHLY_NTH_WEEKDAY"
                ? recurrence.weekOfMonth
                : null,
            endDate: recurrence.endDate,
          },
        },
      });
      const payload = result.data?.updateTaskSeriesRule;
      if (!payload?.success) {
        setError(payload?.message ?? "Sarjan muutos epäonnistui.");
        return false;
      }
      setRuleSummary(payload.message);
      return true;
    } catch (err) {
      console.error("Failed to change series rule:", err);
      setError(err instanceof Error ? err.message : "Sarjan muutos epäonnistui.");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Persists a metadata change. Closes the dialog on success unless
   * `closeOnSuccess` is false — used when a dirty tarkastuskohdat edit is
   * about to move the dialog body into the scope step instead. Returns
   * whether the save succeeded.
   */
  const commit = async (
    scope: "single" | "series",
    input: Record<string, unknown>,
    { closeOnSuccess = true }: { closeOnSuccess?: boolean } = {},
  ): Promise<boolean> => {
    if (!task) return false;
    setIsSaving(true);
    setError(null);
    try {
      let updated: TaskExtended | null = null;

      if (scope === "single") {
        const result = await updateTaskMutation({ variables: { input } });
        if (!result.data?.updateTask?.success || !result.data.updateTask.task) {
          setError(
            result.data?.updateTask?.message ??
              "Tallennus epäonnistui. Yritä uudelleen.",
          );
          return false;
        }
        updated = result.data.updateTask.task as TaskExtended;
      } else {
        // Määräaika is per-task by design, so a series save applies it to this
        // task alone and sends only the content fields onward. Doing it first
        // means the series call has to carry the version that write produced.
        let version = input.version as number;
        const { dueDate, ...content } = input;

        if ("dueDate" in input) {
          const dueResult = await updateTaskMutation({
            variables: { input: { id: input.id, version, dueDate } },
          });
          if (
            !dueResult.data?.updateTask?.success ||
            !dueResult.data.updateTask.task
          ) {
            setError(
              dueResult.data?.updateTask?.message ??
                "Määräajan tallennus epäonnistui.",
            );
            return false;
          }
          updated = dueResult.data.updateTask.task as TaskExtended;
          version = updated.version;
        }

        if (Object.keys(content).length > 2) {
          const result = await updateSeriesMutation({
            variables: { input: { ...content, version } },
          });
          if (
            !result.data?.updateTaskSeries?.success ||
            !result.data.updateTaskSeries.task
          ) {
            setError(
              result.data?.updateTaskSeries?.message ??
                "Tallennus epäonnistui. Yritä uudelleen.",
            );
            return false;
          }
          updated = result.data.updateTaskSeries.task as TaskExtended;
        }
      }

      if (updated) {
        setLocalVersion(updated.version);
        // Merge into the original task so callers preserve fields the mutation
        // didn't return (e.g. project, space). Apollo's cache already has the
        // merged record; this just keeps the parent's useState snapshot in sync.
        onSaved?.({ ...task, ...updated });
      }
      setScopeOpen(false);
      if (closeOnSuccess) onOpenChange(false);
      return true;
    } catch (err) {
      console.error("Failed to update task:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Tallennus epäonnistui. Yritä uudelleen.",
      );
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    if (!task) return;

    if (ruleChanged()) {
      const ok = await commitRule();
      if (ok) {
        onOpenChange(false);
        onSaved?.(task);
      }
      return;
    }

    const input = buildInput();

    // Only INTERNAL_MAIN_TASK can recur, never INSPECTION_MAIN_TASK, so a
    // recurring task never carries a tarkastuskohdat section to consider here.
    if (task.isRecurring) {
      if (!input) {
        onOpenChange(false);
        return;
      }
      setScopeOpen(true);
      return;
    }

    if (!input && !rowsDirty) {
      onOpenChange(false);
      return;
    }

    if (input) {
      const ok = await commit("single", input, { closeOnSuccess: !rowsDirty });
      if (!ok) return;
    }

    if (rowsDirty) {
      topicFlow.reset();
      setTopicsFlowActive(true);
    }
  };

  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && topicsFlowActive) {
      // Mirrors the standalone tarkastuskohdat editor's close guard: a
      // synchronous (single-tarkastus) run is short and its result is about
      // to arrive, so block dismissal only for that case. An enqueued
      // (hanke-wide) run may always be dismissed.
      if (topicFlow.submitting && !topicFlow.applyToWholeProject) return;
      if (
        topicFlow.step === "progress" &&
        (topicFlow.displayProgress?.complete || topicFlow.finishedWithoutFrame)
      ) {
        onTopicsRewritten?.();
      }
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="w-screen h-dvh max-w-full max-h-dvh rounded-none flex flex-col overflow-hidden box-border md:w-full md:h-auto md:max-w-[550px] md:max-h-[90dvh] md:rounded-lg">
        <DialogHeader className="text-left pr-8 min-w-0">
          <DialogTitle>Muokkaa tehtävän tietoja</DialogTitle>
          <DialogDescription>
            {topicsFlowActive
              ? inspectionTopicRewriteStepDescription(topicFlow.step)
              : canEditDueDate
                ? "Päivitä tehtävän nimi, vastuuhenkilö ja määräaika."
                : "Päivitä tehtävän nimi ja vastuuhenkilö."}
          </DialogDescription>
        </DialogHeader>

        {!topicsFlowActive && (
          <>
            {/* Body — `min-w-0` on the wrapper AND on every field is what stops
                the horizontal overflow: a grid item defaults to
                `min-width: auto` and so refuses to shrink below its content's
                intrinsic width, which pushed the whole body past the dialog
                edge. `touch-pan-y` and `overscroll-contain` match
                CreateTaskDialog, killing the mobile Safari horizontal-swipe
                wiggle. */}
            <div className="grid gap-4 py-4 px-1 overflow-y-auto overflow-x-hidden min-h-0 min-w-0 touch-pan-y overscroll-contain">
              {/* Title */}
              <div className="grid gap-2 min-w-0">
                <Label htmlFor="edit-task-title">
                  Tehtävän nimi <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="edit-task-title"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  placeholder="Syötä tehtävän nimi"
                  className="w-full max-w-full box-border"
                />
              </div>

              {/* Assigned To — inheritance is one pinned row in the same list as the
                  candidates, so dropping the override is a selection rather than a
                  separate destructive-sounding action, and an inheriting task shows
                  the incoming person as a selected value instead of a placeholder. */}
              <div className="grid gap-2 min-w-0">
                <Label htmlFor="edit-task-assigned">Vastuuhenkilö</Label>
                <Combobox
                  options={users
                    .filter((u) => u.isActive)
                    .map((user) => ({
                      value: user.id,
                      label:
                        user.firstName && user.lastName
                          ? `${user.firstName} ${user.lastName}`
                          : user.email,
                    }))}
                  pinnedOption={inheritOption}
                  value={
                    formData.responsibleUserId ||
                    (canClearResponsible ? INHERIT_VALUE : null)
                  }
                  // Null arrives when the selected person is clicked again to
                  // deselect; landing on inheritance is the right outcome there.
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      responsibleUserId:
                        value === null || value === INHERIT_VALUE ? "" : value,
                    })
                  }
                  placeholder="Valitse vastuuhenkilö"
                  searchPlaceholder="Hae henkilöä..."
                  disabled={isSaving}
                  className="w-full min-w-0"
                />
              </div>

              {/* Due Date — DeadlineField rather than a raw <input type="date">,
                  which carries a large intrinsic minimum width (the overflow's
                  root cause) and looked different from the create dialog. */}
              {canEditDueDate && (
                <div className="grid gap-2 min-w-0">
                  <Label>Määräaika</Label>
                  <DeadlineField
                    value={formData.dueDate}
                    onChange={(dueDate) => setFormData({ ...formData, dueDate })}
                  />
                </div>
              )}

              {task?.isRecurring && (
                <RecurrenceFields
                  value={recurrence}
                  onChange={setRecurrence}
                  startYmd={task.occurrenceDate ?? ""}
                  disabled={isSaving}
                />
              )}

              {/* Tarkastuskohdat — only once the subtasks have loaded; see
                  `topicsLoaded` above for why an unloaded list is left out
                  entirely rather than shown as empty. */}
              {showTopics && topicsLoaded && (
                <div className="grid gap-2 min-w-0 pt-2 border-t">
                  <Label>Tarkastuskohdat</Label>
                  <InspectionTopicRowList
                    rows={rows}
                    onUpdateRow={(key, name) =>
                      setRows((prev) => updateTopicRowName(prev, key, name))
                    }
                    onRemoveRow={(key) =>
                      setRows((prev) => removeTopicRow(prev, key))
                    }
                    onMoveRow={(index, direction) =>
                      setRows((prev) => moveTopicRow(prev, index, direction))
                    }
                    onAddRow={() => setRows((prev) => addTopicRow(prev))}
                    disabled={isSaving}
                  />
                  {rowsValidationError && (
                    <p className="text-xs text-red-600">{rowsValidationError}</p>
                  )}
                </div>
              )}
              {showTopics && !topicsLoaded && (
                <p className="text-xs text-muted-foreground pt-2 border-t">
                  Ladataan tarkastuskohtia…
                </p>
              )}
            </div>

            {ruleSummary && (
              <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
                {ruleSummary}
              </div>
            )}

            {error && (
              <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSaving}
              >
                Peruuta
              </Button>
              <Button
                onClick={handleSave}
                disabled={
                  !formData.title.trim() ||
                  isSaving ||
                  (rowsDirty && !!rowsValidationError)
                }
              >
                {isSaving && <SpinnerIcon className="mr-2 h-4 w-4 animate-spin" />}
                {isSaving ? "Tallennetaan..." : "Tallenna"}
              </Button>
            </DialogFooter>
          </>
        )}

        {topicsFlowActive && task && (
          <InspectionTopicRewriteSteps
            flow={topicFlow}
            taskId={task.id}
            rows={rows}
            onBack={() => setTopicsFlowActive(false)}
            onClosed={() => {
              onOpenChange(false);
              onTopicsRewritten?.();
            }}
            onDismiss={() => handleDialogOpenChange(false)}
          />
        )}

        <SeriesScopeDialog
          open={scopeOpen}
          onOpenChange={setScopeOpen}
          mode="edit"
          busy={isSaving}
          onChoose={(scope) => {
            const input = buildInput();
            if (input) void commit(scope, input);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
